import React, {useMemo} from "react";
import {defineStepper} from "@stepperize/react";
import ProjectCreationContextProvider
    from "./context/project-creation-context-provider.tsx";
import useProjectCreationContext from "./context/use-project-creation-context.ts";
import getStepsForFlowMode from "./stepper/create-project-steps.ts";
import renderCreateProjectStep from "./steps/render-create-project-step.tsx";
import {BaseDialogProps} from "@/components/dialogs/base-dialog";
import ClosableDialog from "../dialogs/closable-dialog";
import CreateProjectTypeStep from "./steps/create-project-type-step.tsx";

const CreateProjectDialogLayout: React.FC<Omit<BaseDialogProps, 'children'>> = ({title, open, onClose}) => {
    
    const { flowModeCreationProject, isCreatingProject } = useProjectCreationContext();

    const dialogSizeClass = flowModeCreationProject != null
        ? 'max-w-[45vw] h-[30rem]'
        : 'max-w-lg h-[22rem]';

    const steps = useMemo(
        () => {
            if (flowModeCreationProject == null) {
                return null;
            }

            return getStepsForFlowMode(flowModeCreationProject);
        },
        [flowModeCreationProject],
    );

    const stepperDefinition = useMemo(
        () => {
            if (steps == null || steps.length === 0) {
                return null;
            }

            return defineStepper(steps);
        },
        [steps],
    );
    
    const Stepper = stepperDefinition?.Stepper;

    return (
        <ClosableDialog title={title} open={open} onClose={onClose} sizeClass={dialogSizeClass} closeOnOutsideClick={false} showCloseButton={!isCreatingProject}>
            {Stepper == null ? (
                <CreateProjectTypeStep />
            ) : (
                <Stepper.Root linear className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    {({stepper}) => {
                        const progress = Math.round(
                            ((stepper.index + 1) / stepper.count) * 100,
                        );

                        return (
                            <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
             
                                <Stepper.Content step={stepper.current.id} className='flex-1 min-h-0 overflow-hidden'>
                                    {renderCreateProjectStep(stepper)}
                                </Stepper.Content>

                                {stepper.count < 1 && 
                                    <div className="h-1.25 w-full shrink-0 overflow-hidden rounded-full bg-muted mt-2">
                                        <div className="h-full rounded-full bg-primary transition-all duration-300 bg-indigo-600"
                                             style={{width: `${progress}%`}}/>
                                    </div>
                                }
                            </div>
                        );
                    }}
                </Stepper.Root>
            )}
        </ClosableDialog>
    );
}

const CreateProjectDialog: React.FC<Omit<BaseDialogProps, 'children'>> = ({title, open, onClose}) => (
    <ProjectCreationContextProvider>
        <CreateProjectDialogLayout title={title} open={open} onClose={onClose} />
    </ProjectCreationContextProvider>
)

export default CreateProjectDialog;